import React, { useId } from "react";
import "@fontsource/jetbrains-mono/800.css";
import "@fontsource/crimson-text/400-italic.css";
import type { AotdDay } from "../../printsApi";
import type { PrintEdition } from "../../printProducts";
import { engineLabel, MILESTONES, printDate, printQuote, quoteScale, raceOffset } from "./faceData";
import "./cardFace.css";

// The front of a trading card, as the print file draws it (front Ca2). `mm` is the size of one
// millimetre in px: 63 x 88 mm, so mm=5 draws a 315 x 440 px card. With the author's quote the art
// is 2.5 mm smaller and the quote sits under it as a caption.

const HOLO_STOPS: [number, string][] = [
  [0, "hsl(0 95% 78%)"], [12, "hsl(40 100% 72%)"], [24, "hsl(90 85% 72%)"], [37, "hsl(170 90% 70%)"],
  [50, "hsl(220 95% 78%)"], [63, "hsl(285 90% 80%)"], [76, "hsl(330 95% 80%)"], [88, "hsl(360 95% 78%)"], [100, "hsl(40 100% 72%)"],
];

/** The original's big number: the static rainbow with its ink outline, in mm units like the print file. */
const HoloNumber: React.FC<{ n: number; mm: number }> = ({ n, mm }) => {
  const id = useId().replace(/:/g, "");
  const fs = 15.3;
  const txt = String(n);
  const ch = txt.length;
  const cx = (ch * 0.6 * fs) / 2 + ch * 0.6 * fs * 0.25;
  const base = 0.86 * fs;
  return (
    <svg width={55.8 * mm} height={fs * mm} viewBox={`0 0 55.8 ${fs}`} aria-label={txt}>
      <defs>
        <linearGradient id={`hf${id}`} x1="0" y1="0" x2="1" y2=".55">
          {HOLO_STOPS.map(([o, c]) => <stop key={o} offset={`${o}%`} stopColor={c} />)}
        </linearGradient>
      </defs>
      <g transform={`translate(${cx} 0) scale(1.5 1) translate(${-cx} 0)`}>
        <text x={cx} y={base} textAnchor="middle" fontSize={fs} fill="#222" stroke="#222" strokeWidth={0.56}>{txt}</text>
        <text x={cx} y={base} textAnchor="middle" fontSize={fs} fill={`url(#hf${id})`}>{txt}</text>
      </g>
    </svg>
  );
};

export interface CardFaceProps {
  day: AotdDay;
  art: string;
  edition?: PrintEdition;
  mm?: number;
}

export const CardFace: React.FC<CardFaceProps> = ({ day, art, edition = "print", mm = 5 }) => {
  const orig = edition === "original";
  const tier = MILESTONES[day.number];
  const ver = engineLabel(day.engineVersion);
  const quote = printQuote(day);
  return (
    <div
      className={`pf-card${orig ? " pf-card--orig" : ""}${quote ? " has-q" : ""}`}
      style={{ "--mm": `${mm}px` } as React.CSSProperties}
    >
      <div className="pf-art"><img src={art} alt="" /></div>
      <div className="pf-vline">
        <span>{printDate(day.date)}</span>
        {orig && <span className="pf-tag">original 1/1</span>}
      </div>
      {quote && (
        <div className="pf-q" style={{ "--qs": String(quoteScale(quote)) } as React.CSSProperties}>{quote}</div>
      )}
      <div className="pf-big">
        {orig ? (
          <HoloNumber n={day.number} mm={mm} />
        ) : (
          <b className={`pf-no${tier ? " is-milestone" : ""}`} data-tier={tier} style={{ "--chars": String(day.number).length } as React.CSSProperties}>
            <span className="pf-n">{day.number}</span>
          </b>
        )}
      </div>
      <div className="pf-foot">
        <h3 className="pf-title">{day.title}</h3>
        <div className="pf-info">
          <span className="pf-t">{raceOffset(day.authorOffsetMs)}</span>
          {ver && <><span className="pf-sep">·</span>{ver}</>}
        </div>
        <div className="pf-r">
          <span className="pf-emos">{(day.emojis ?? []).join("")}</span>
          <span className="pf-who">
            {day.authorEmoji && <><span className="pf-emo">{day.authorEmoji}</span><span className="pf-dot">.</span></>}
            {day.createdBy}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CardFace;
